import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";

/**
 * MatchAlertPrefsPanel — profile card for the AI Match Insight alert opt-in.
 *
 * Lets the user enable/disable strong-match emails and pick the score
 * threshold (0-100). Values persist server-side via the existing
 * `/api/auth/preferences` endpoint so they stay in sync across devices.
 */
export default function MatchAlertPrefsPanel() {
  const { t } = useT();
  const { user, checkAuth } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [threshold, setThreshold] = useState(80);
  const [saving, setSaving] = useState(false);

  // Hydrate from the user record. `user.match_alerts_enabled` may be
  // undefined for legacy accounts — fall back to the safe "on with 80"
  // default that the backend uses for new records.
  useEffect(() => {
    if (user) {
      setEnabled(user.match_alerts_enabled ?? true);
      setThreshold(Math.max(0, Math.min(100, Number(user.match_alert_threshold ?? 80))));
    }
  }, [user]);

  const persist = async (patch) => {
    setSaving(true);
    try {
      await api.patch("/auth/preferences", patch);
      await checkAuth();
      toast.success(t("prefMatchAlertSaved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ed-card p-6" data-testid="match-alert-prefs">
      <p className="ed-eyebrow flex items-center gap-2">
        <Sparkles size={12} strokeWidth={1.75} />
        {t("prefMatchAlertsEyebrow")}
      </p>
      <h3 className="font-serif text-xl mt-2">{t("prefMatchAlertsTitle")}</h3>
      <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
        {t("prefMatchAlertsBody")}
      </p>

      <label className="mt-5 flex items-center justify-between gap-4 border-t border-neutral-200 pt-5">
        <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-700">
          {t("prefMatchAlertsToggle")}
        </span>
        <input
          type="checkbox"
          data-testid="match-alert-toggle"
          checked={enabled}
          disabled={saving}
          onChange={(e) => {
            const v = e.target.checked;
            setEnabled(v);
            persist({ match_alerts_enabled: v });
          }}
          className="w-4 h-4 accent-neutral-900"
        />
      </label>

      <div className={`mt-5 ${enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <div className="flex items-baseline justify-between gap-2 mb-2">
          <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-700">
            {t("prefMatchAlertThreshold")}
          </span>
          <span
            data-testid="match-alert-threshold-value"
            className="font-serif text-2xl"
          >
            {threshold}
            <span className="font-mono text-xs text-neutral-500 ml-1">/100</span>
          </span>
        </div>
        <input
          type="range"
          data-testid="match-alert-threshold-input"
          min={40}
          max={100}
          step={5}
          value={threshold}
          disabled={!enabled || saving}
          onChange={(e) => setThreshold(Number(e.target.value))}
          onMouseUp={() => persist({ match_alert_threshold: threshold })}
          onTouchEnd={() => persist({ match_alert_threshold: threshold })}
          className="w-full accent-neutral-900"
        />
        <div className="flex justify-between font-mono text-[0.55rem] text-neutral-500 uppercase tracking-[0.1em] mt-1">
          <span>40</span>
          <span>70</span>
          <span>100</span>
        </div>
      </div>
    </div>
  );
}
